import * as React from "react"

const MAX_ATTEMPTS = 3

/**
 * Hook that waits for an element with the specified ID to appear in the DOM.
 * Polls at the specified interval until the element is found or max attempts reached.
 *
 * @param id - The ID of the element to wait for
 * @param interval - The polling interval in milliseconds (default: 100ms)
 * @returns The found HTMLElement or null if not found after max attempts
 */
export function useWaitForElement(id: string, interval = 100): HTMLElement | null {
  const [element, setElement] = React.useState<HTMLElement | null>(null)
  const attemptCountRef = React.useRef(0)

  React.useEffect(() => {
    const checkElement = () => {
      const el = document.getElementById(id)
      if (el) {
        setElement(el)
        return true
      }

      attemptCountRef.current++
      return attemptCountRef.current >= MAX_ATTEMPTS
    }

    // Check immediately
    if (checkElement()) return

    // Start polling if element not found
    const intervalId = window.setInterval(() => {
      if (checkElement()) {
        clearInterval(intervalId)
      }
    }, interval)

    return () => clearInterval(intervalId)
  }, [id, interval])

  return element
}
